const WEEKDAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export type StudioClock = {
  timeZone: string | null;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekdayIndex: number;
  weekdayName: string;
  minutesFromMidnight: number;
  ymd: string;
};

export type StudioGreeting = "Good morning" | "Good afternoon" | "Good evening";

let runtimeTenantTimeZone: string | null = null;

/** Remember the loaded tenant zone for helpers that run outside React. */
export function setRuntimeTenantTimeZone(
  timeZone: string | null | undefined,
): void {
  runtimeTenantTimeZone = resolveIanaTimeZone(timeZone);
}

export function getRuntimeTenantTimeZone(): string | null {
  return runtimeTenantTimeZone;
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function partValue(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): string {
  return parts.find((part) => part.type === type)?.value ?? "";
}

/** Returns a valid IANA zone, or null to fall back to the browser clock. */
export function resolveIanaTimeZone(
  timeZone: string | null | undefined,
): string | null {
  const tz = timeZone?.trim();
  if (!tz) return null;
  try {
    Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
    return tz;
  } catch {
    return null;
  }
}

function clockFromLocalDate(now: Date, timeZone: string | null): StudioClock {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const weekdayIndex = now.getDay();
  const weekdayName =
    Object.keys(WEEKDAY_INDEX).find(
      (name) => WEEKDAY_INDEX[name] === weekdayIndex,
    ) ?? "Sunday";

  return {
    timeZone,
    year,
    month,
    day,
    hour,
    minute,
    weekdayIndex,
    weekdayName,
    minutesFromMidnight: hour * 60 + minute,
    ymd: `${year}-${pad2(month)}-${pad2(day)}`,
  };
}

/**
 * Civil date and clock in the studio time zone.
 * Class wall-clock times (`11:00 AM`) should be compared to this clock, not the viewer's.
 */
export function getStudioClock(
  now: Date = new Date(),
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): StudioClock {
  const tz = resolveIanaTimeZone(timeZone);
  if (!tz) return clockFromLocalDate(now, null);

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  let hour = Number(partValue(parts, "hour"));
  if (hour === 24) hour = 0;
  const minute = Number(partValue(parts, "minute"));
  const year = Number(partValue(parts, "year"));
  const month = Number(partValue(parts, "month"));
  const day = Number(partValue(parts, "day"));
  const weekdayName = partValue(parts, "weekday");
  const weekdayIndex = WEEKDAY_INDEX[weekdayName] ?? 0;

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return clockFromLocalDate(now, tz);
  }

  return {
    timeZone: tz,
    year,
    month,
    day,
    hour,
    minute,
    weekdayIndex,
    weekdayName,
    minutesFromMidnight: hour * 60 + minute,
    ymd: `${year}-${pad2(month)}-${pad2(day)}`,
  };
}

/** Local-midnight Date for the studio's current calendar day (for comparing calendar cells). */
export function studioTodayDate(clock: StudioClock): Date {
  return new Date(clock.year, clock.month - 1, clock.day, 0, 0, 0, 0);
}

export function studioGreeting(hour: number): StudioGreeting {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function startOfLocalDay(value: Date): Date {
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function isStudioTodayDate(date: Date, clock: StudioClock): boolean {
  return startOfLocalDay(date).getTime() === studioTodayDate(clock).getTime();
}

export function isStudioPastDate(date: Date, clock: StudioClock): boolean {
  return startOfLocalDay(date).getTime() < studioTodayDate(clock).getTime();
}

export function formatYmdInTimeZone(
  date: Date,
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  return getStudioClock(date, timeZone).ymd;
}

export function studioTodayYmd(
  now: Date = new Date(),
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  return getStudioClock(now, timeZone).ymd;
}

/** UTC Date whose ISO date is studio today, so OpenAPI DateOnly keeps the civil day. */
export function studioTodayBillingDate(
  now: Date = new Date(),
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): Date {
  return new Date(`${studioTodayYmd(now, timeZone)}T00:00:00.000Z`);
}

export function formatStudioLongDate(
  now: Date = new Date(),
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  const tz = resolveIanaTimeZone(timeZone);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz ?? undefined,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(now);
}

/** Civil YYYY-MM-DD bounds for a calendar month, without UTC shifting local midnight. */
export function ymdRangeForCalendarMonth(
  year: number,
  month1to12: number,
): { fromDate: string; toDate: string } {
  const lastDay = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  return {
    fromDate: `${year}-${pad2(month1to12)}-01`,
    toDate: `${year}-${pad2(month1to12)}-${pad2(lastDay)}`,
  };
}

export function addDaysToYmd(ymd: string, days: number): string {
  const [year, month, day] = ymd.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function calendarDaysBetweenYmd(fromYmd: string, toYmd: string): number {
  const [y1, m1, d1] = fromYmd.split("-").map(Number);
  const [y2, m2, d2] = toYmd.split("-").map(Number);
  const from = Date.UTC(y1, m1 - 1, d1);
  const to = Date.UTC(y2, m2 - 1, d2);
  return Math.round((to - from) / 86_400_000);
}

/** YYYY-MM-DD for a calendar cell built from local year/month/day (studio month grid). */
export function civilCalendarCellYmd(
  year: number,
  monthIndex0: number,
  day: number,
): string {
  return `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
}

/** YYYY-MM-DD from a Date used as a calendar cell (local civil components). */
export function civilCalendarDateYmd(date: Date): string {
  return civilCalendarCellYmd(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
}

export function formatStudioHHmm(
  date: Date,
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): string {
  const clock = getStudioClock(date, timeZone);
  return `${pad2(clock.hour)}:${pad2(clock.minute)}`;
}
